'use client';

import {
  LoginWithRedirectOptions,
  useCommonAuthProvider,
} from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { isExplicitInviteEntryUrl } from '@modules/auth/utils/invite-utils';
import { useFlagsProxy } from '@modules/feature-flags/hooks/useFlagsProxy';
import { markAppEntry } from '@modules/shared/analytics/scripts/fullstoryUtils';
import { COOKIE_NAMES } from '@utils/constants';
import { AbsoluteSpinner } from 'app/_components/AbsoluteSpinner';

import { getCookie } from 'cookies-next';
import React, { useEffect } from 'react';
import { IdentityProvider } from 'shared/src/util';

/**
 * sessionStorage key used to track whether a silent auth attempt (`prompt=none`)
 * has already been tried for this tab. Prevents infinite redirect loops when
 * Auth0 has no active session — the first attempt returns `login_required`,
 * and the flag ensures we fall through to an interactive login on the next try.
 */
export const SILENT_AUTH_ATTEMPTED_KEY = 'SILENT_AUTH_ATTEMPTED';

/**
 * sessionStorage key that stores the original page URL before the silent auth
 * redirect. When `prompt=none` fails with `login_required`, the callback page
 * navigates back to this URL so AuthGuard can re-extract context params
 * (connection, login_hint, etc.) and pass them to the interactive login.
 */
export const SILENT_AUTH_RETURN_TO_KEY = 'SILENT_AUTH_RETURN_TO';

const isAuth0Environment = (): boolean =>
  !process.env.AUTH_CLIENT_PROVIDER || process.env.AUTH_CLIENT_PROVIDER === IdentityProvider.AUTH0;

/**
 * OIDC `appState.returnTo` passed through `/callback`. Use `/` on the bare app root
 * so callback treats it like `/` (not an explicit deep link) and runs membership-based
 * routing (single-org shortcut, `/organizations`). Non-root URLs keep `href` so query
 * strings and paths are preserved.
 */
const getOidcStateReturnTo = (): string => {
  const { pathname, search } = window.location;
  const isRootPath = pathname === '/' || pathname === '';

  if (isRootPath && search === '') {
    return '/';
  }

  return window.location.toString();
};

/**
 * Routes an unauthenticated visitor either through a silent `prompt=none` check
 * or straight to interactive login. For Auth0 environments the silent attempt
 * replicates the Auth0 SDK's `checkSession()` behavior for cross-app SSO
 * (e.g. Leader → Maestro): a user with an active Auth0 session gets tokens
 * without seeing a login form. If there is no session, the callback receives
 * `login_required`, the `SILENT_AUTH_ATTEMPTED` flag stays set, and the next
 * pass falls through to interactive login.
 *
 * Shared by `AuthGuard` (deep-linked authenticated routes) and the root index
 * page so both entry points behave identically.
 */
export const attemptSilentAuthOrLogin = ({
  login,
  loginWithRedirect,
  cloudMspEnableUiFeatures,
}: {
  login: (params?: LoginWithRedirectOptions) => void | Promise<void>;
  loginWithRedirect: (params?: LoginWithRedirectOptions) => void | Promise<void>;
  cloudMspEnableUiFeatures: boolean;
}): void => {
  const loginParams: LoginWithRedirectOptions = { returnTo: getOidcStateReturnTo() };

  if (cloudMspEnableUiFeatures) {
    const params = new URLSearchParams(window.location.search);
    const connection = params.get('connection');

    if (connection) {
      loginParams.connection = connection;
    }

    const loginHint = params.get('login_hint');

    if (loginHint) {
      loginParams.loginHint = loginHint;
    }
  }

  // A recent Cloud Portal logout sets cribl_auth0_logout (cleared on the next successful /callback).
  // When the Leader bounces an unauthenticated user back here (e.g. manage/?from=<leaderUrl>) right
  // after that logout, Auth0's SSO session may still be alive — a silent prompt=none would
  // re-authenticate immediately and silently undo the logout. While this cookie is present, skip
  // silent auth and force an interactive prompt=login so the logout actually sticks. The cookie is
  // consumed by /callback once the user logs back in, so normal SSO logins are not affected.
  const loggedOutFromPortal = Boolean(getCookie(COOKIE_NAMES.AUTH0_LOGOUT));

  const silentAuthAttempted = sessionStorage.getItem(SILENT_AUTH_ATTEMPTED_KEY);
  const shouldAttemptSilentAuth =
    isAuth0Environment() &&
    !silentAuthAttempted &&
    !loggedOutFromPortal &&
    !isExplicitInviteEntryUrl(window.location.pathname, window.location.search);

  if (shouldAttemptSilentAuth) {
    sessionStorage.setItem(SILENT_AUTH_ATTEMPTED_KEY, '1');
    sessionStorage.setItem(SILENT_AUTH_RETURN_TO_KEY, window.location.toString());
    loginWithRedirect({ ...loginParams, prompt: 'none' });

    return;
  }

  // Reaching here means we are NOT attempting silent auth — either it was already
  // tried this pass (flag set after a prompt=none rejection bounced us back), it
  // does not apply (Okta/invite), or the user just logged out from the portal. Clear
  // the per-tab flag before the interactive redirect so a future fresh entry to this
  // origin can retry prompt=none instead of being permanently locked into the login
  // form. Loop-safe: the flag is read above to decide the path, so clearing it here
  // cannot re-enter silent auth within the same bounce.
  sessionStorage.removeItem(SILENT_AUTH_ATTEMPTED_KEY);
  login(loggedOutFromPortal ? { ...loginParams, prompt: 'login' } : loginParams);
};

export const AuthGuard: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, user, isLoading, login, loginWithRedirect } = useCommonAuthProvider();
  const [isRedirecting, setRedirecting] = React.useState(false);
  const { cloudMspEnableUiFeatures } = useFlagsProxy();

  // Fallback for users who deep-link directly into an authenticated route,
  // bypassing page.tsx. The sessionStorage gate in markAppEntry ensures this
  // fires at most once per session even if both page.tsx and AuthGuard are mounted.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    markAppEntry(isAuthenticated);
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (isLoading || isRedirecting) {
      return;
    }

    if (!isAuthenticated) {
      setRedirecting(true);
      attemptSilentAuthOrLogin({ login, loginWithRedirect, cloudMspEnableUiFeatures });
    }
  }, [isAuthenticated, isLoading]);

  if (isRedirecting || isLoading || !isAuthenticated || !user) {
    return <AbsoluteSpinner />;
  }

  return <>{children}</>;
};
